import {SaveButton} from 'shells/button/save-button/save-button';
import {ButtonCard} from 'shells/card/button-card';
import {SegmentedControl} from 'shells/card/segmented-control';
import {FormField} from 'shells/form/form-field';
import {GridList} from 'shells/grid-list/grid-list';
import {DoubleColumnIcon} from 'shells/icon/svg/double-column-icon';
import {SingleColumnIcon} from 'shells/icon/svg/single-column-icon';
import {Stack} from 'shells/layout/stack/stack';
import {LoadingPage} from 'shells/loading-page/loading-page';
import {TextField} from 'shells/textfield/textfield';
import {Body} from 'shells/typography/body';

import type {GetAiGeneratedTemplateByEdition_aiGeneratedTemplateGet_editions_edges_node as AiGeneratedEdition} from 'nutshell-graphql-types';

import styles from './ai-design-form.module.css';

type Props = {
    aiCampaignId: string;
    selectedEditionId: string;
    editions: AiGeneratedEdition[];
    websiteUrl: string;
    websiteUrlError: string | null | undefined;
    campaignDescription: string;
    campaignLayout: string;
    campaignStyle: string;
    isAnyGenerating: boolean;
    isRegenerating: boolean;
    isLoadingOutcomeLimits: boolean;
    hasProblem: boolean;
    onRegenerate: () => void;
    onWebsiteUrlChange: (websiteUrl: string) => void;
    onCampaignDescriptionChange: (campaignDescription: string) => void;
    onCampaignLayoutChange: (campaignLayout: string) => void;
    onCampaignStyleChange: (campaignStyle: string) => void;
    onSelectEdition: (editionId: string) => void;
};

export function AiDesignForm(props: Props) {
    return (
        <Stack spacing={16}>
            <Stack spacing={2}>
                <Body isBold={true} color='dark'>
                    Preview and edit
                </Body>
                <Body size='small'>Make adjustments to this campaign</Body>
            </Stack>
            <div className={styles['fields-container']}>
                {props.isAnyGenerating && <div className={styles.overlay} />}
                <Stack spacing={16}>
                    <FormField
                        innerId='url-input'
                        label={
                            <Body size='small' color='dark' isBold={true}>
                                Use design and content from
                            </Body>
                        }
                        tooltipInfo='Uses design and content from your website to generate a broadcast.'
                        errorMessage={props.websiteUrlError}
                        noMargin={true}
                    >
                        <TextField
                            placeholder='e.g. https://mybusiness.com/blog/announcing-new-product'
                            value={props.websiteUrl}
                            onChange={props.onWebsiteUrlChange}
                            focusOnMount={true}
                        />
                    </FormField>
                    <FormField
                        innerId='campaign-description-input'
                        label={
                            <Body size='small' color='dark' isBold={true}>
                                Describe your campaign
                            </Body>
                        }
                        tooltipInfo='The more details you share, the more personalized and impactful your campaign will be.'
                        noMargin={true}
                    >
                        <TextField
                            placeholder='E.g., Announce our winter sale with 30% off all products'
                            multiline={true}
                            value={props.campaignDescription}
                            onChange={props.onCampaignDescriptionChange}
                        />
                    </FormField>
                </Stack>
            </div>
            <div className={styles['fields-container']}>
                {props.isAnyGenerating && <div className={styles.overlay} />}
                {!NutClientConfig.hasCampaignGeneratorV2FF && (
                    <>
                        <Stack spacing={4}>
                            <Body isBold={true} color='dark' size='small'>
                                Campaign layout
                            </Body>
                            <Stack spacing={16}>
                                <SegmentedControl
                                    hasPadding={false}
                                    variant='separated'
                                    buttonOptions={[
                                        {
                                            value: 'single-column',
                                            content: (
                                                <div className='px-8'>
                                                    <Stack
                                                        spacing={8}
                                                        isHorizontal={true}
                                                        alignItems='center'
                                                    >
                                                        {/* @ts-expect-error */}
                                                        <SingleColumnIcon wrapWithDiv={false} />
                                                        <Body size='small' color='dark'>
                                                            Single column
                                                        </Body>
                                                    </Stack>
                                                </div>
                                            ),
                                        },
                                        {
                                            value: 'double-column',
                                            content: (
                                                <div className='px-8'>
                                                    <Stack
                                                        spacing={8}
                                                        isHorizontal={true}
                                                        alignItems='center'
                                                    >
                                                        {/* @ts-expect-error */}
                                                        <DoubleColumnIcon wrapWithDiv={false} />
                                                        <Body size='small' color='dark'>
                                                            Double column
                                                        </Body>
                                                    </Stack>
                                                </div>
                                            ),
                                        },
                                    ]}
                                    selectedValue={props.campaignLayout}
                                    setSelectedValue={props.onCampaignLayoutChange}
                                />
                            </Stack>
                        </Stack>
                        <Stack spacing={4}>
                            <Body isBold={true} color='dark' size='small'>
                                Select campaign style
                            </Body>
                            <SegmentedControl
                                hasPadding={false}
                                buttonOptions={[
                                    {
                                        title: 'Modern',
                                        value: 'modern',
                                    },
                                    {
                                        title: 'Classic',
                                        value: 'classic',
                                    },
                                    {
                                        title: 'Minimal',
                                        value: 'minimal',
                                    },
                                ]}
                                selectedValue={props.campaignStyle}
                                setSelectedValue={props.onCampaignStyleChange}
                            />
                        </Stack>
                    </>
                )}
            </div>
            <div className={styles['fields-container']}>
                {props.editions.every((e) => e.status === 'GENERATING') && (
                    <div className={styles.overlay} />
                )}
                {NutClientConfig.hasCampaignGeneratorV2FF && (
                    <Stack spacing={4}>
                        <Body isBold={true} color='dark' size='small'>
                            Generated designs
                        </Body>
                        <GridList numColumns={2} gridGap={8}>
                            {props.editions.map((edition) => {
                                const editionWithStatus = edition as AiGeneratedEdition & {
                                    status?: string;
                                };
                                const isEditionGenerating =
                                    editionWithStatus.status === 'GENERATING';
                                return (
                                    <div key={edition.id} className={styles.button}>
                                        <ButtonCard
                                            isSelected={props.selectedEditionId === edition.id}
                                            className='br-3'
                                            isFullWidth={true}
                                            isDisabled={isEditionGenerating}
                                            showDisabledOverlay={isEditionGenerating}
                                            onClick={() => props.onSelectEdition(edition.id)}
                                        >
                                            {isEditionGenerating ? (
                                                <LoadingPage />
                                            ) : edition.emailTemplate?.thumbnailUrl ? (
                                                <img
                                                    src={edition.emailTemplate.thumbnailUrl}
                                                    alt='Edition thumbnail'
                                                    style={{width: '100%', borderRadius: '3px'}}
                                                />
                                            ) : null}
                                        </ButtonCard>
                                    </div>
                                );
                            })}
                        </GridList>
                    </Stack>
                )}
            </div>
            <SaveButton
                size='big'
                variant='ai-purple-light'
                buttonText='Regenerate'
                savingText='Regenerating…'
                errorSaving={Boolean(props.hasProblem || props.websiteUrlError)}
                resetOnComplete={true}
                disabled={props.isAnyGenerating || props.isLoadingOutcomeLimits}
                isSaving={props.isRegenerating}
                // @ts-expect-error
                onClick={props.onRegenerate}
            />
        </Stack>
    );
}
